const SIZE_TYPE_ORDER = {
  Short: 1,
  Regular: 2,
  Tall: 3,
  Courte: 1, // Fr Short
  Régulière: 2, // Fr Regular
  Grande: 3, // Fr Tall
};

/**
 * Converts a Map to an array for serialization.
 * @param {Map} map Map to convert.
 * @returns {Array} Array of map entries.
 */
export const mapToArrayForSerialization = (map) =>
  map instanceof Map
    ? [...map.entries()].reduce((acc, entry) => {
        acc.push(entry);
        return acc;
      }, [])
    : [];

/**
 * Adds inseam entries to a Map by productId.
 * @param {Array} inseams Array of: shape { inseam: string, productId: string, productUrl: string }
 * @param {Map} productIdToInseamMap Initial Map
 * @returns {Map} Updated Map
 */
export const addInseamsToMap = (inseams, productIdToInseamMap) => {
  const newProductIdToInseamMap = new Map(productIdToInseamMap);

  inseams?.forEach((inseam = {}) => {
    if (!newProductIdToInseamMap.has(inseam.productId)) {
      newProductIdToInseamMap.set(inseam.productId, inseam);
    }
  });

  return newProductIdToInseamMap;
};

function getUpdatedLinkedProducts({
  productId,
  linkedProductId,
  inseamsGraph,
}) {
  const linkedProducts = inseamsGraph.get(productId) || [];
  return linkedProducts.includes(linkedProductId)
    ? linkedProducts
    : [...linkedProducts, linkedProductId];
}

export const addInseamsToGraphBidirectionally = (
  productId,
  inseams,
  inseamsGraph
) => {
  const newInseamsGraph = new Map(inseamsGraph);

  inseams.forEach((inseam) => {
    const updatedLinkedProducts = getUpdatedLinkedProducts({
      productId,
      linkedProductId: inseam.productId,
      inseamsGraph: newInseamsGraph,
    });
    newInseamsGraph.set(productId, updatedLinkedProducts);

    const updatedReverseLinkedProducts = getUpdatedLinkedProducts({
      productId: inseam.productId,
      linkedProductId: productId,
      inseamsGraph: newInseamsGraph,
    });
    newInseamsGraph.set(inseam.productId, updatedReverseLinkedProducts);
  });

  return newInseamsGraph;
};

/**
 * Depth First Search algorithm to traverse the inseams graph.
 * @param {Map} graph Adjacency list representation of the inseams graph. Key: productId, Value: array of productIds.
 * @param {String} initialNode productId to start the traversal.
 * @param {Set} visitedNodes List to keep track of visited inseams: the cluster.
 * @returns {Set} Set of visited inseams.
 */
function dfs(graph, initialNode, visitedNodes = new Set()) {
  visitedNodes.add(initialNode);
  const linkedNodes = graph.get(initialNode) || [];

  linkedNodes?.forEach((node) => {
    if (!visitedNodes.has(node)) {
      dfs(graph, node, visitedNodes);
    }
  });

  return visitedNodes;
}

function parseInseamNumber(inseam = "") {
  return parseInt(inseam.replace(/[^\d]/g, ""));
}

const mapSizeTypeToOrder = (inseam) => SIZE_TYPE_ORDER[inseam];

// An inseam could be numeric like `23"` or a size type like "Tall", as for now.
// If we'd need to check if it's a size type, we could do:
// const isSizeTypeInseam = SIZE_TYPE_ORDER[firstInseamValue] > 0; // e.g. Tall
const getSortedInseams = (inseams) => {
  const firstInseamValue = inseams[0]?.inseam;
  const isNumericInseam = !!parseInseamNumber(firstInseamValue); // e.g. 23"

  const mapInseamToNumber = isNumericInseam
    ? parseInseamNumber
    : mapSizeTypeToOrder;

  const sortFunction = (a, b) =>
    mapInseamToNumber(a.inseam) - mapInseamToNumber(b.inseam);

  return inseams.sort(sortFunction);
};

/**
 * Removes duplicate inseams preferring FP over MD products.
 * 17 21 23_MD 23_FP 25 -> 17 21 23_FP 25
 * @param {Array} sortedInseams Array of inseams sorted by inseam.
 * @returns Array of deduplicated inseams.
 */
const getDeduplicatedInseams = (sortedInseams = []) => {
  if (sortedInseams.length < 2) {
    return sortedInseams;
  }
  const deduplicatedInseams = [sortedInseams[0]];

  for (let index = 1; index < sortedInseams.length; index++) {
    const currentInseam = sortedInseams[index];
    const lastAddedIndex = deduplicatedInseams.length - 1;
    const lastAddedInseam = deduplicatedInseams[lastAddedIndex];

    if (currentInseam.inseam === lastAddedInseam.inseam) {
      const isCurrentInseamPreferred =
        lastAddedInseam.productUrl.includes("-MD") &&
        !currentInseam.productUrl.includes("-MD");
      if (isCurrentInseamPreferred) {
        deduplicatedInseams[lastAddedIndex] = currentInseam;
      }
    } else {
      deduplicatedInseams.push(currentInseam);
    }
  }

  return deduplicatedInseams;
};

export const getMergedInseams = ({
  productId,
  inseamsGraph,
  productIdToInseamMap,
}) => {
  if (!inseamsGraph?.length || !productIdToInseamMap?.length) {
    return [];
  }
  const inseamsGraphMap = new Map(inseamsGraph);
  const mergedProductIds = dfs(inseamsGraphMap, productId);
  if (!mergedProductIds.size) {
    return [];
  }
  const pidToInseamMap = new Map(productIdToInseamMap);
  const mergedInseams = [...mergedProductIds]
    .map((productId) => pidToInseamMap.get(productId))
    .filter(Boolean);
  const sortedInseams =
    mergedInseams.length > 1 ? getSortedInseams(mergedInseams) : mergedInseams;
  return getDeduplicatedInseams(sortedInseams);
};
